import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import Fetch from 'i18next-fetch-backend'
import { startTransition } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { I18nextProvider, initReactI18next } from 'react-i18next'
import { HydratedRouter } from 'react-router/dom'
import { getInitialNamespaces } from 'remix-i18next/client'
import { DEFAULT_LOCALE, supportedLocales } from './utils/i18n.ts'

if (ENV.MODE === 'production' && ENV.SENTRY_DSN) {
	void import('./utils/monitoring.client.tsx').then(({ init }) => init())
}

// The react-router-devtools client is injected into root.tsx, so every route
// rendered through root boots its own devtools instance. The rrdt "Styleguide"
// tab iframes the live `/styleguide` route (which renders through root), so the
// framed page spawns a *nested* devtools panel - and each click into its
// Styleguide tab descends another level, stacking a new panel every time.
// rrdt has no per-route opt-out, so we suppress devtools whenever the document
// is framed: the top window keeps full devtools, the embedded route gets none,
// which breaks the recursion. (Dev-only; devtools aren't bundled in prod.)
if (ENV.MODE !== 'production' && window.self !== window.top) {
	const style = document.createElement('style')
	style.textContent =
		'#tanstack_devtools,:has(> [data-testid="react-router-devtools-trigger"]){display:none !important}'
	document.head.append(style)
}

async function hydrate() {
	await i18next
		.use(initReactI18next)
		.use(Fetch)
		.use(LanguageDetector)
		.init({
			defaultNS: 'common',
			fallbackLng: DEFAULT_LOCALE,
			supportedLngs: [...supportedLocales],
			// The server already resolved the locale and stamped `<html lang>`;
			// read it back from there so client and server agree (no re-detection,
			// no flash of the wrong language on hydration).
			detection: { order: ['htmlTag'], caches: [] },
			// Load only the namespaces the matched routes declared (their
			// `handle.i18n`), lazily, from the dictionary resource route.
			ns: getInitialNamespaces(),
			backend: { loadPath: '/resources/locales/{{lng}}/{{ns}}' },
			interpolation: { escapeValue: false },
			// The template ships its own dictionaries; silence the CDN nudge.
			showSupportNotice: false,
		})

	startTransition(() => {
		hydrateRoot(
			document,
			<I18nextProvider i18n={i18next}>
				<HydratedRouter />
			</I18nextProvider>,
		)
	})
}

void hydrate()
